import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import { Brand } from "custom-types/Brand";
import useDomain from "hooks/useDomain";
import formatImgixUrl from "utils/image/formatImgixUrl";

import { SailthruMetaTags } from "../SailthruMetaTags";

const titleCase = (s: string) => {
  const nonCapitalizedWords = ["and", "or"];
  const words = s.split(" ");
  return words
    .map((word) => {
      if (nonCapitalizedWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.replace(/./, (match) => match.toUpperCase());
    })
    .join(" ");
};

export const BrandHead: React.FC<{
  brand: Brand;
  categoryName?: string;
  page?: number;
  brandsNew?: boolean;
  children?: JSX.Element[] | JSX.Element | string;
}> = ({ brand, categoryName, page }) => {
  const router = useRouter();
  const domain = useDomain();

  const currentPath = `${domain}${router.asPath}`;
  const { description, logo, name, tagline } = brand;
  const title = categoryName
    ? `${name} ${titleCase(categoryName)} on Leafly${
        Number(page) > 1 ? "| Page " + page : ""
      }`
    : `${[name, tagline].filter((s) => !!s).join(": ")} | Leafly`;

  const generalDescription = categoryName
    ? `Find your favorite ${name} ${categoryName} while discovering what's new. See what's available near you for pickup or delivery!${
        Number(page) > 1 ? " Page " + page : ""
      }`
    : `Learn about ${name} including who they are, their products, and where you can find them.`;
  const shortDescription =
    description?.length > 200 ? `${description.slice(0, 199)}…` : description;
  const logoUrl = formatImgixUrl(logo);
  const tags = [
    name,
    "brand-page",
    ...brand.availableStates,
    ...brand.availableCountries,
  ];

  const canonicalUrl = currentPath.split("?")[0];

  return (
    <Head>
      <title>{title}</title>

      <link
        rel="canonical"
        href={
          // TODO: add pagination here once we have it
          canonicalUrl
        }
      />
      <meta name="description" content={generalDescription} />

      {logoUrl && (
        <meta property="og:image" content={`${logoUrl}?w=1200&fm=jpg`} />
      )}
      <meta
        property="og:description"
        content={shortDescription || generalDescription}
      />
      <meta property="og:title" content={title} />
      <meta property="og:type" content="webpage" />

      <link
        rel="alternate"
        href={`https://www.leafly.ca${router.asPath}`}
        hrefLang="en-ca"
      />
      <link
        rel="alternate"
        href={`https://www.leafly.com${router.asPath}`}
        hrefLang="en-us"
      />

      <SailthruMetaTags
        description={shortDescription || generalDescription}
        imageUrl={logoUrl}
        tags={tags}
        title={title}
      />
    </Head>
  );
};
